/**
 * What a failed write means. One table, for every surface.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §6.1.
 *
 * WHY CLASSIFY AT ALL, rather than retrying everything. Three of these outcomes
 * are "stop", and retrying them is worse than not retrying: a 404 row will never
 * come back, a 400 body will not become acceptable on the third try, and a
 * sealing refusal never reached the wire in the first place. Blanket retry would
 * turn each of those into a loop against an answer that cannot change.
 */
import {
  SEALED_WRITE_ERRORS,
  isEncryptedWriteRequiredError,
  isSealedWriteError,
} from '@/utils/courses-encryption/sealed-writes';
import { isStaleRevisionError } from '@/utils/courses-encryption/annotation-revisions';

export const OUTCOME = Object.freeze({
  /** Try again, and count it toward the attempt cap. */
  RETRY: 'RETRY',
  /**
   * Try again, and do NOT count it. For "there is no network": a member in a
   * tunnel has not used up their attempts, and counting it would park a row for
   * being somewhere with no signal. This is also why there is no separate
   * offline state — an offline entry keeps a backoff timer, and the timer is the
   * wake.
   *
   * And for the two states that are about to resolve themselves the same way: a
   * status this device could not read yet, and an enrolment it has not caught up
   * with. Neither is a verdict on the body, and both end.
   */
  RETRY_UNCOUNTED: 'RETRY_UNCOUNTED',
  /**
   * Another device wrote first. Not a plain retry — replaying the same body
   * against the same revision would be refused again, and an envelope replaces
   * the whole row rather than merging into it. The engine holds the slot and
   * `onAutosaveFailure` decides: the row being typed in refetches and resends,
   * a replay from the outbox takes the server's version.
   */
  CONFLICT: 'CONFLICT',
  /** Give up on this write. */
  DROP: 'DROP',
  /** Give up on the ROW: nothing we send will revive it. */
  DROP_UNSAVABLE: 'DROP_UNSAVABLE',
  /** Refused before it was sent, and deterministically so. */
  REFUSE: 'REFUSE',
});

/** Statuses that mean "the server is having a moment", not "your write is wrong". */
const TRANSIENT = new Set([408, 425, 429, 500, 502, 503, 504]);

/**
 * @param {*} error whatever the sender rejected with
 * @returns {string} one of OUTCOME
 */
export function classify(error) {
  // Deterministic and local: the sealing hook refused, so nothing was sent and
  // nothing will be. Rule 2's one exception — the caller restores.
  //
  // Except two that are not a verdict on the write at all.
  //
  // STATUS_UNKNOWN: the hook could not READ whether the member is enrolled (the
  // status request failed, or failed recently and is in its backoff), so it
  // does not know whether to seal. That is the shape of "no network", and it is
  // treated as such — the slot keeps its body and tries again, uncounted, which
  // is also what the copy for it promises ("check your connection and try
  // again"). As a refusal it dropped the slot, so the words stayed on screen but
  // were never sent.
  //
  // NO_REVISION: the hook knows the row but not its revision. For a live slot
  // that has exactly one cause — a stale 409 made `forceThroughConflict` forget
  // the number, and a send arrived before its refetch put a fresh one back: the
  // idle timer after the member's next keystroke, or any `flush` (blur, page
  // turn, drawer close, sign-out). The revision is on its way. As a refusal
  // this dropped the slot and rolled a blank back, losing the text being typed
  // during a conflict. COUNTED, unlike STATUS_UNKNOWN: a row that genuinely was
  // never read has no refetch coming, and should park after the attempt cap
  // and say "not saved" rather than retry for ever.
  if (isSealedWriteError(error)) {
    if (error.code === SEALED_WRITE_ERRORS.STATUS_UNKNOWN) return OUTCOME.RETRY_UNCOUNTED;
    if (error.code === SEALED_WRITE_ERRORS.NO_REVISION) return OUTCOME.RETRY;
    return OUTCOME.REFUSE;
  }

  const status = error?.response?.status;

  // No response at all: offline, DNS, connection refused, or a timeout with
  // nothing to show for it. `axios` gives these no `response`.
  if (status === undefined || status === null) return OUTCOME.RETRY_UNCOUNTED;

  // A write names the revision it was based on, and the server's row has moved
  // on. What happens next depends on where the write came from, which is the
  // store's to decide (`onAutosaveFailure`): a row the member is typing in
  // refetches and forces through, a replay from the outbox yields.
  if (isStaleRevisionError(error)) return OUTCOME.CONFLICT;

  // The member is enrolled and this write went up in the clear — the device did
  // not know yet when it sealed. Not a verdict on the body: the same body,
  // sealed, is accepted. Matched on the API's error CODE, like every other
  // branch on a wire error here; `sealed-writes.js` says why, and why it is
  // uncounted.
  if (isEncryptedWriteRequiredError(error)) return OUTCOME.RETRY_UNCOUNTED;

  // The row is gone — deleted on another device, or a stale id from a cache.
  if (status === 404) return OUTCOME.DROP_UNSAVABLE;

  // A 401 here is NOT the first one. `utils/interceptors/401-error` already
  // refreshes the token once and replays the request, and logs the member out
  // when that fails — so by the time one reaches this classifier the session is
  // already ending, and a retry would be a write under a dead token. The plan's
  // §6.1 has this as "retry once, after the interceptor refreshes"; the
  // interceptor turned out to BE that once.
  if (status === 401) return OUTCOME.DROP;

  if (TRANSIENT.has(status)) return OUTCOME.RETRY;

  // 400, 422, 403, and anything else the server means as a verdict on the
  // request. A 409 reaching here is not a stale revision — a rotation's
  // ROTATION_PENDING and a duplicate tag name both answer 409 without the code.
  if (status >= 400 && status < 500) return OUTCOME.DROP;

  // An unrecognised 5xx is still the server's problem, not the body's.
  return OUTCOME.RETRY;
}

/** Whether this outcome means the engine should schedule another attempt. */
export const isRetryable = outcome =>
  outcome === OUTCOME.RETRY || outcome === OUTCOME.RETRY_UNCOUNTED;
